import React from 'react';
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetOverlay } from '@frontend/shadcn/components/ui/sheet';
import { CalendarIcon, XmarkIcon, PencilIcon, CheckIcon } from '@revfluence/fresh-icons/solid/esm';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@frontend/shadcn/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@frontend/shadcn/components/ui/accordion';
import dayjs from 'dayjs';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@frontend/shadcn/components/ui/tabs';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { ChevronRightIcon, InfoIcon, MegaphoneIcon, PlusIcon } from '@revfluence/fresh-icons/regular/esm';
import { Label } from '@frontend/shadcn/components/ui/label';
import { Input } from '@frontend/shadcn/components/ui/input';
import { LayersIcon } from '@radix-ui/react-icons';
import { FieldErrors, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { logger } from '@common';
import { GlobeIcon, TowerBroadcastIcon, FlagIcon, FileLinesIcon, BookIcon } from '@revfluence/fresh-icons/solid';
import { AutoComplete } from '@revfluence/fresh';
import { AutoCompleteProps } from 'antd';
import { type ContentPreviewData, type ContentPreviewSheetProps as BaseContentPreviewSheetProps } from './types';
import { PlatformIcon, PostTypeLabel } from './components/content-preview-header';
import {
  CreatorAvatar,
  CreatorInfo,
  CreatorActionDownload,
  CreatorActionShare,
  CreatorActionsContainer,
  CreatorContainer,
  CreatorAvatarContainer,
} from './components/content-preview-creator';
import ContentPreviewContent from './components/content-preview-content';
import { MetricCard, MetricCardLabel, MetricCardValue } from './components/metric-card';
import { PerformanceHeader, PerformanceTitle, PerformanceDateRange } from './components/performance-header';
import {
  SheetGrid,
  SheetTopBar,
  SheetColumn,
  PreviewSheetContent,
  PreviewSheetHeading,
  PreviewSheetCaption,
  PreviewSheetLabel,
  PreviewSheetSection,
  PreviewSheetBadgeContainer,
  PreviewSheetBadge,
  PreviewSheetEmpty,
  MetricsGrid,
  SheetHeader,
  SheetTitle,
} from './components/content-preview-sheet';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/shadcn/components/ui/form';
import ContentPreviewAlert, { IAlertProps } from './components/content-preview-alert';

import { boostPostSchema, type BoostPostFormValues } from './types/boost-post-schema';

const { useState } = React;

const compactFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

interface ContentPreviewSheetProps extends Omit<BaseContentPreviewSheetProps, 'content'> {
  content: ContentPreviewData & {
    onEditProject?: () => void;
    onEditGroup?: () => void;
    onEditMusic?: () => void;
    onEditRetailer?: () => void;
    onEditBrands?: () => void;
  };
}

const utmFields = [
  { label: 'Source', icon: <GlobeIcon />, placeholder: 'google' },
  { label: 'Medium', icon: <TowerBroadcastIcon />, placeholder: 'social' },
  { label: 'Campaign', icon: <FlagIcon />, placeholder: 'project_name' },
  { label: 'Term', icon: <BookIcon />, placeholder: 'creator_name' },
  { label: 'Content', icon: <FileLinesIcon />, placeholder: 'creator_id_username' },
];

const contentPreviewAlertProps = () => {
  // other message types to hook up
//   <ContentPreviewAlert type="unAuthorized">
//   <span>
//     You are not authorized to boost this post on Instagram.
//     If you proceed to boost the post, we can attempt to contact the creator on your behalf to obtain the necessary permission and then proceed with your ad creation request.
//   </span>
// </ContentPreviewAlert>
// <ContentPreviewAlert type="error">
//   <span>Reels containing tappable elements can’t be used for ads. Choose a different post to create an ad.</span>
// </ContentPreviewAlert>
  const type: IAlertProps['type'] = 'success';
  const content = <span>You are authorized to boost this post on Instagram</span>;
  return { type, content };
};
const useBoost = (content: ContentPreviewData, availableMetaAdAccounts?: { id: string }[]) => {
  const boostPostForm = useForm<BoostPostFormValues>({
    resolver: zodResolver(boostPostSchema),
    defaultValues: {
      campaign: { type: 'existing' },
      post: { postExternalId: content.postExternalId, platform: content.metadata.platform === 'instagram' ? 'instagram' : undefined, },
      // ...other default values
    },
    mode: 'onTouched'
  });
  const campaignType = boostPostForm.watch('campaign.type');
  const campaignId = boostPostForm.watch('campaign.campaignId');
  const campaignName = boostPostForm.watch('campaign.name');
  const campaignObjective = boostPostForm.watch('campaign.objective');

  // Set default ad account to the first available
  React.useEffect(() => {
    if (
      availableMetaAdAccounts &&
      availableMetaAdAccounts.length > 0 &&
      !boostPostForm.getValues('adAccount.id')
    ) {
      boostPostForm.setValue('adAccount.id', availableMetaAdAccounts[0].id);
    }
  }, [availableMetaAdAccounts, boostPostForm]);

  const { formState } = boostPostForm;
  const { errors } = formState;
  const isCampaignValid =
    campaignType === 'existing'
      ? !!campaignId && !errors.campaign
      : !!campaignName && !!campaignObjective && !errors.campaign;

  return { boostPostForm, isCampaignValid };
};

// Enum for campaign objectives
const CAMPAIGN_OBJECTIVES = [
  { value: 'OUTCOME_APP_PROMOTION', label: 'App Promotion' },
  { value: 'OUTCOME_AWARENESS', label: 'Awareness' },
  { value: 'OUTCOME_ENGAGEMENT', label: 'Engagement' },
  { value: 'OUTCOME_LEADS', label: 'Leads' },
  { value: 'OUTCOME_SALES', label: 'Sales' },
  { value: 'OUTCOME_TRAFFIC', label: 'Traffic' },
];

export function ContentPreviewSheetBoost({ content, open, onOpenChange, availableProjects, availableGroups, availableMetaAdAccounts, children, onBoostPost }: Readonly<ContentPreviewSheetProps>) {
  const { boostPostForm, isCampaignValid } = useBoost(content, availableMetaAdAccounts);

  const [options, setOptions] = useState<AutoCompleteProps['options']>([]);
  const allOptions = [
    { label: 'creator_ig_username', value: 'creator_ig_username' },
    { label: 'creator_username', value: 'creator_username' },
    { label: 'creator_tiktok_username', value: 'creator_tiktok_username' },
    { label: 'project_name', value: 'project_name' },
    { label: 'creator_username', value: 'creator_username' },
    { label: 'link_offer_name', value: 'link_offer_name' },
  ];
  const campaignType = boostPostForm.watch('campaign.type');
  const adSetType = boostPostForm.watch('adSet.type');
  const selectedAdAccountId = boostPostForm.watch('adAccount.id');
  const selectedCampaignId = boostPostForm.watch('campaign.campaignId');
  const selectedAdAccount = availableMetaAdAccounts?.find((metaAdAccount) => metaAdAccount.id === selectedAdAccountId);
  const availableAdCampaigns = selectedAdAccount?.campaigns;
  const selectedAdCampaign = availableAdCampaigns?.find((adCampaign) => adCampaign.id === selectedCampaignId);
  const availableAdSets = selectedAdCampaign?.adSets;

  const onBoostPostSubmit = (values: BoostPostFormValues) => {
    logger.info('Boost Post Form Submitted:', values);
    // handle submit
    onBoostPost?.(values);
  };

  const onBoostPostError = (errors: FieldErrors<BoostPostFormValues>) => {
    logger.error('Boost Post Form Errors:', errors);
  };

  const onBoostPostCancel = () => {
    logger.info('Boost Post Form Canceled');
    boostPostForm.reset();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {children && <SheetTrigger asChild>{children}</SheetTrigger>}
      <SheetOverlay className="z-[190]" />
      <SheetContent
        className="w-full overflow-y-auto p-0 z-[200] sm:max-w-[1000px] [&>button:first-of-type]:hidden flex flex-col"
        side="right"
      >
        <div className="flex flex-col h-full flex-1">
          <SheetHeader>
            <SheetTitle>Content Preview</SheetTitle>
            <SheetClose asChild>
              <Button variant="ghost" size="icon" aria-label="Close preview" onClick={() => onOpenChange(false)}>
                <XmarkIcon className="h-4 w-4" />
              </Button>
            </SheetClose>
          </SheetHeader>

          <SheetTopBar>
            <div className="flex items-center gap-2">
              <PlatformIcon platform={content.metadata.platform} />
              <PostTypeLabel postType={content.metadata.postType} />
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="flex items-center gap-2 rounded-lg">
                <CalendarIcon className="h-3 w-3" />
                <span>
                  {new Date(content.createdAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </Badge>
            </div>
          </SheetTopBar>

          <SheetGrid>
            {/* Left column - Creator, Content and Caption */}
            <SheetColumn>
              <PreviewSheetContent>
                <CreatorContainer>
                  <CreatorAvatarContainer>
                    <CreatorAvatar name={content.creator.name} avatarUrl={content.creator.avatarUrl} />
                    <CreatorInfo name={content.creator.name} handle={content.creator.handle} />
                  </CreatorAvatarContainer>
                  <CreatorActionsContainer>
                    <CreatorActionDownload onClick={content.onDownload} />
                    <CreatorActionShare
                      onClick={() => {
                        if (content.onShare) {
                          content.onShare();
                        } else {
                          window.open(window.location.href, '_blank', 'noopener,noreferrer');
                        }
                      }}
                    />
                  </CreatorActionsContainer>
                </CreatorContainer>

                <ContentPreviewContent
                  mediaUrl={content.mediaUrl}
                  contentType={content.metadata.contentType}
                  originalUrl={content.originalUrl}
                />

                <div>
                  <PreviewSheetHeading>Post Caption</PreviewSheetHeading>
                  <PreviewSheetCaption caption={content.caption} />
                </div>
              </PreviewSheetContent>
            </SheetColumn>

            {/* Right column - Tabs, Metrics and Details */}
            <SheetColumn>
              <Tabs defaultValue="overview" className="w-full h-full flex flex-col">
                <div className="border-b mt-3">
                  <TabsList className="border-0 bg-transparent m-0">
                    <TabsTrigger
                      value="overview"
                      className="relative data-[state=active]:text-gray-900 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-gray-900 rounded-none bg-transparent data-[state=active]:bg-transparent"
                    >
                      Overview
                    </TabsTrigger>
                    <TabsTrigger
                      value="boost"
                      className="relative data-[state=active]:text-gray-900 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-gray-900 rounded-none bg-transparent data-[state=active]:bg-transparent"
                    >
                      Boost
                    </TabsTrigger>
                  </TabsList>
                </div>
                <TabsContent value="overview" className="mt-4">
                  <Accordion
                    type="multiple"
                    defaultValue={['performance', 'info', 'additional']}
                    className="space-y-4"
                  >
                    <AccordionItem value="performance" className="">
                      <AccordionTrigger className="p-0 hover:no-underline">
                        <PerformanceHeader className="w-full">
                          <PerformanceTitle>Post Performance</PerformanceTitle>
                          <PerformanceDateRange>
                            {content.metrics.metadata.startDate && content.metrics.metadata.endDate && (
                            <>
                              {dayjs(content.metrics.metadata.startDate).format('MMM D, YYYY')} -{' '}
                              {dayjs(content.metrics.metadata.endDate).format('MMM D, YYYY')}
                            </>
                              )}
                          </PerformanceDateRange>
                        </PerformanceHeader>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <MetricsGrid>
                          <MetricCard className="border-[#4F46E5]">
                            <MetricCardLabel>Impressions</MetricCardLabel>
                            <MetricCardValue>{compactFormatter.format(content.metrics.impressions)}</MetricCardValue>
                          </MetricCard>

                          <MetricCard className="border-[#E11D48]">
                            <MetricCardLabel>Engagement</MetricCardLabel>
                            <MetricCardValue>{compactFormatter.format(content.metrics.engagement)}</MetricCardValue>
                          </MetricCard>

                          <MetricCard className="border-[#EC4899]">
                            <MetricCardLabel>Engagement %</MetricCardLabel>
                            <MetricCardValue>{content.metrics.engagementRate.toFixed(1)}%</MetricCardValue>
                          </MetricCard>

                          <MetricCard className="border-[#059669]">
                            <MetricCardLabel>TMV</MetricCardLabel>
                            <MetricCardValue>{currencyFormatter.format(content.metrics.tmv ?? 0)}</MetricCardValue>
                          </MetricCard>
                        </MetricsGrid>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="info" className="">
                      <AccordionTrigger className="p-0 hover:no-underline">
                        <h3 className="text-md font-medium">Info</h3>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <div className="space-y-4">
                          <PreviewSheetSection>
                            <PreviewSheetLabel>Project</PreviewSheetLabel>
                            <PreviewSheetBadgeContainer>
                              {content.projectEditingState?.isEditing ? (
                                <div className="flex items-center gap-2 w-full">
                                  <Select
                                    value={content.projectEditingState.projectId?.toString() ?? ''}
                                    onValueChange={(value) => {
                                      if (availableProjects) {
                                        const selectedProject = availableProjects.find((p) => p.id.toString() === value);
                                        if (selectedProject && content.projectEditingState) {
                                          // Just update the state, don't save yet
                                          content.projectEditingState.updateState?.({
                                            projectId: selectedProject.id.toString(),
                                            projectName: selectedProject.name
                                          });
                                        }
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-8">
                                      <SelectValue placeholder="Select a project" />
                                    </SelectTrigger>
                                    <SelectContent className="z-[1000]">
                                      {availableProjects?.map((project) => (
                                        <SelectItem key={project.id} value={project.id.toString()}>
                                          {project.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      if (content.projectEditingState?.onSave &&
                                          content.projectEditingState.projectId &&
                                          content.projectEditingState.projectName) {
                                        content.projectEditingState.onSave(
                                          content.projectEditingState.projectId,
                                          content.projectEditingState.projectName
                                        );
                                      }
                                    }}
                                  >
                                    <CheckIcon className="h-4 w-4 text-green-600" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      if (content.projectEditingState?.onCancel) {
                                        content.projectEditingState.onCancel();
                                      }
                                    }}
                                  >
                                    <XmarkIcon className="h-4 w-4 text-red-600" />
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  {content.metadata.project ? (
                                    <PreviewSheetBadge className="bg-blue-500 hover:bg-blue-600">
                                      {content.metadata.project.name}
                                    </PreviewSheetBadge>
                                  ) : (
                                    <PreviewSheetEmpty />
                                  )}
                                  {content.onEditProject && (
                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={content.onEditProject}>
                                      <PencilIcon className="h-4 w-4 text-gray-900" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </PreviewSheetBadgeContainer>
                          </PreviewSheetSection>

                          <PreviewSheetSection>
                            <PreviewSheetLabel>Group</PreviewSheetLabel>
                            <PreviewSheetBadgeContainer>
                              {content.groupEditingState?.isEditing ? (
                                <div className="flex items-center gap-2 w-full">
                                  <Select
                                    value={content.groupEditingState.groupId?.toString() ?? ''}
                                    onValueChange={(value) => {
                                      if (availableGroups) {
                                        const selectedGroup = availableGroups.find((g) => g.id.toString() === value);
                                        if (selectedGroup && content.groupEditingState) {
                                          // Just update the state, don't save yet
                                          content.groupEditingState.updateState?.({
                                            groupId: selectedGroup.id.toString(),
                                            groupName: selectedGroup.name
                                          });
                                        }
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-8">
                                      <SelectValue placeholder="Select a group" />
                                    </SelectTrigger>
                                    <SelectContent className="z-[1000]">
                                      {availableGroups?.map((group) => (
                                        <SelectItem key={group.id} value={group.id.toString()}>
                                          {group.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      if (content.groupEditingState?.onSave &&
                                          content.groupEditingState.groupId &&
                                          content.groupEditingState.groupName) {
                                        content.groupEditingState.onSave(
                                          content.groupEditingState.groupId,
                                          content.groupEditingState.groupName
                                        );
                                      }
                                    }}
                                  >
                                    <CheckIcon className="h-4 w-4 text-green-600" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={content.groupEditingState?.onCancel}
                                  >
                                    <XmarkIcon className="h-4 w-4 text-red-600" />
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  {content.metadata.group ? (
                                    <PreviewSheetBadge className="bg-pink-500 hover:bg-pink-600">
                                      {content.metadata.group.name}
                                    </PreviewSheetBadge>
                                  ) : (
                                    <PreviewSheetEmpty />
                                  )}
                                  {content.onEditGroup && (
                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={content.onEditGroup}>
                                      <PencilIcon className="h-4 w-4 text-gray-900" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </PreviewSheetBadgeContainer>
                          </PreviewSheetSection>

                          <PreviewSheetSection>
                            <PreviewSheetLabel>Last Updated Date</PreviewSheetLabel>
                            <div className="text-sm">
                              {new Date(content.updatedAt || content.createdAt).toLocaleDateString()}
                            </div>
                          </PreviewSheetSection>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </TabsContent>
                <TabsContent value="boost" className="mt-4 flex flex-col h-full">
                  <ContentPreviewAlert {...contentPreviewAlertProps()}>
                    {contentPreviewAlertProps().content}
                  </ContentPreviewAlert>

                  <Form {...boostPostForm}>
                    <form
                      className="flex flex-col flex-1"
                      onSubmit={boostPostForm.handleSubmit(onBoostPostSubmit, onBoostPostError)}
                    >
                      <div className="flex-1 space-y-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-md font-medium text-gray-900">Boost Post</h3>
                          <FormField
                            control={boostPostForm.control}
                            name="adAccount.id"
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Select value={field.value} onValueChange={field.onChange}>
                                    <SelectTrigger className="w-[200px]">
                                      <SelectValue placeholder="Select Ad Account" />
                                    </SelectTrigger>
                                    <SelectContent className="z-[1000]">
                                      {availableMetaAdAccounts?.map((metaAdAccount) => (
                                        <SelectItem key={metaAdAccount.id} value={metaAdAccount.id}>
                                          {metaAdAccount.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <div className="rounded-lg border border-gray-200 p-4">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                              <MegaphoneIcon className="h-4 w-4 text-gray-600" />
                              <h4 className="font-medium m-0 p-0 text-gray-900">Campaigns</h4>
                            </div>
                            {campaignType === 'new' ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-1"
                                onClick={() => boostPostForm.setValue('campaign.type', 'existing')}
                                type="button"
                              >
                                <PlusIcon className="h-4 w-4" />
                                <span>Select Existing Campaign</span>
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-1"
                                onClick={() => boostPostForm.setValue('campaign.type', 'new')}
                                type="button"
                              >
                                <PlusIcon className="h-4 w-4" />
                                <span>New Campaign</span>
                              </Button>
                            )}
                          </div>
                          <div className="space-y-4">
                            {campaignType === 'existing' ? (
                              <FormField
                                control={boostPostForm.control}
                                name="campaign.campaignId"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Select Campaign</FormLabel>
                                    <FormControl>
                                      <Select
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        defaultValue={field.value}
                                      >
                                        <SelectTrigger id="campaign-select" className="w-full">
                                          <SelectValue placeholder="Choose an existing campaign" />
                                        </SelectTrigger>
                                        <SelectContent className="z-[1000]">
                                          {availableAdCampaigns?.map((adCampaign) => (
                                            <SelectItem key={adCampaign.id} value={adCampaign.id}>
                                              {adCampaign.name}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            ) : (
                              <>
                                <FormField
                                  control={boostPostForm.control}
                                  name="campaign.name"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Campaign Name</FormLabel>
                                      <FormControl>
                                        <Input placeholder="Enter campaign name" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={boostPostForm.control}
                                  name="campaign.objective"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Objective</FormLabel>
                                      <FormControl>
                                        <Select
                                          value={field.value}
                                          onValueChange={field.onChange}
                                          defaultValue={field.value}
                                        >
                                          <SelectTrigger id="new-campaign-objective" className="w-full">
                                            <SelectValue placeholder="Select objective" />
                                          </SelectTrigger>
                                          <SelectContent className="z-[1000]">
                                            {CAMPAIGN_OBJECTIVES.map((obj) => (
                                              <SelectItem key={obj.value} value={obj.value}>{obj.label}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </>
                            )}
                            <div className="rounded-lg bg-gray-50 p-4">
                              <div className="flex items-center gap-2 mb-3">
                                <InfoIcon className="h-4 w-4 text-gray-500" />
                                <span className="text-sm text-gray-600">
                                  Selected campaign details will appear here
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div />
                        <div className="rounded-lg border border-gray-200 p-4 flex flex-col gap-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <LayersIcon className="h-4 w-4 text-gray-600" />
                              <h4 className="font-medium m-0 p-0 text-gray-900">Ad Set</h4>
                            </div>
                            {!isCampaignValid && <ChevronRightIcon className="h-4 w-4 text-gray-600 disabled" />}
                            {isCampaignValid && (adSetType === 'existing' ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-1"
                                onClick={() => boostPostForm.setValue('adSet.type', 'new')}
                                type="button"
                              >
                                <PlusIcon className="h-4 w-4" />
                                <span>New Ad Set</span>
                              </Button>
                                  ) : (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="flex items-center gap-1"
                                      onClick={() => boostPostForm.setValue('adSet.type', 'existing')}
                                      type="button"
                                    >
                                      <PlusIcon className="h-4 w-4" />
                                      <span>Select Existing Ad Set</span>
                                    </Button>
                                  ))}
                          </div>
                          {isCampaignValid && (
                          <div className="space-y-4">
                            {adSetType === 'existing' ? (
                              <div className="flex flex-col gap-2">
                                <Label htmlFor="adset-select">Select Ad Set</Label>
                                <Select>
                                  <SelectTrigger id="adset-select" className="w-full">
                                    <SelectValue placeholder="Choose an existing ad set" />
                                  </SelectTrigger>
                                  <SelectContent className="z-[1000]">
                                    {availableAdSets?.map((adSet) => (
                                      <SelectItem key={adSet.id} value={adSet.id}>
                                        {adSet.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <div className="rounded-lg bg-gray-50 p-4">
                                  <div className="flex items-center gap-2 mb-3">
                                    <InfoIcon className="h-4 w-4 text-gray-500" />
                                    <span className="text-sm text-gray-600">
                                      Selected ad set details will appear here
                                    </span>
                                  </div>
                                </div>
                              </div>
                                ) : (
                                  <>
                                    <div className="flex flex-col gap-2">
                                      <Label htmlFor="adset-name">Ad Set Name</Label>
                                      <Input
                                        id="adset-name"
                                        placeholder="Enter ad set name"
                                        className="w-full"
                                      />
                                    </div>
                                    <div className="flex flex-col gap-2">
                                      <Label htmlFor="daily-budget">Daily Budget</Label>
                                      <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                        <Input
                                          id="daily-budget"
                                          type="number"
                                          placeholder="0.00"
                                          className="pl-8"
                                          min="1"
                                          step="0.01"
                                        />
                                      </div>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                      <Label htmlFor="target-audience">Target Audience</Label>
                                      <Select>
                                        <SelectTrigger id="target-audience">
                                          <SelectValue placeholder="Select target audience" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="saved-audience-1">Saved Audience 1</SelectItem>
                                          <SelectItem value="saved-audience-2">Saved Audience 2</SelectItem>
                                          <SelectItem value="custom">Create Custom Audience</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                      <Label htmlFor="placement">Ad Placement</Label>
                                      <Select>
                                        <SelectTrigger id="placement">
                                          <SelectValue placeholder="Choose placement" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="automatic">Automatic Placement</SelectItem>
                                          <SelectItem value="feed">News Feed</SelectItem>
                                          <SelectItem value="stories">Stories</SelectItem>
                                          <SelectItem value="reels">Reels</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </>
                                )}
                          </div>
                            )}
                        </div>
                        <div className="rounded-lg border border-gray-200 p-4 flex flex-col gap-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <LayersIcon className="h-4 w-4 text-gray-600" />
                              <h4 className="font-medium m-0 p-0 text-gray-900">Configure Ad</h4>
                            </div>
                            {!isCampaignValid && <ChevronRightIcon className="h-4 w-4 text-gray-600 disabled" />}
                          </div>
                          {isCampaignValid && (
                          <div className="space-y-4">
                            <div className="flex flex-col gap-2">
                              <Label htmlFor="ad-name-select">Ad Name</Label>
                              <Select>
                                <SelectTrigger id="ad-name-select" className="w-full">
                                  <SelectValue placeholder="Enter ad name" />
                                </SelectTrigger>
                                <SelectContent className="z-[1000]">
                                  <SelectItem value="adset1">High Engagement Users</SelectItem>
                                  <SelectItem value="adset2">Lookalike Audience</SelectItem>
                                  <SelectItem value="adset3">Retargeting Group</SelectItem>
                                </SelectContent>
                              </Select>
                              <div className="flex flex-col gap-2">
                                <Label htmlFor="adset-action">Call to Action</Label>
                                <Select>
                                  <SelectTrigger id="adset-action" className="w-full">
                                    <SelectValue placeholder="Choose an existing ad set" />
                                  </SelectTrigger>
                                  <SelectContent className="z-[1000]">
                                    <SelectItem value="learn_more">Learn More</SelectItem>
                                    <SelectItem value="shop_now">Shop Now</SelectItem>
                                    <SelectItem value="sign_up">Sign Up</SelectItem>
                                    <SelectItem value="contact_us">Contact Us</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="flex flex-col gap-2">
                                <Label htmlFor="destination-url">Destination URL</Label>
                                <Input id="destination-url" placeholder="Creators promotional page" />
                              </div>
                              <Label htmlFor="utm">UTM</Label>
                              <div className="flex flex-col gap-2">
                                {utmFields.map(({ label, icon, placeholder }) => (
                                  <div
                                    key={label}
                                    className="flex items-center rounded-md border text-muted-foreground bg-muted"
                                  >
                                    <div className="flex items-center gap-2 text-muted-foreground bg-muted text-sm w-1/3">
                                      <span className="pl-2">{icon}</span>
                                      <span>{label}</span>
                                    </div>
                                    <AutoComplete
                                      options={options}
                                      onSelect={() => console.log('selected')}
                                      dropdownStyle={{ borderRadius: 0 }}
                                      className="!rounded-none !border-none [&_.ant-select-selector]:!rounded-none"
                                      style={{
                                        width: '100%',
                                        borderLeft: '1px solid #d9d9d9', // antd default border color
                                        borderTop: 'none',
                                        borderRight: 'none',
                                        borderBottom: 'none',
                                        borderRadius: 0,
                                      }}
                                      onSearch={(text) => setOptions(allOptions.filter((opt) => opt.label.toLowerCase().includes(text.toLowerCase())))}
                                      placeholder={placeholder}
                                    />
                                  </div>
  ))}

                                <button
                                  type="button"
                                  className="text-sm text-blue-600 hover:underline mt-1 self-start"
                                >
                                  + Add Custom UTM
                                </button>
                              </div>

                            </div>
                          </div>
                      )}
                        </div>
                      </div>
                      {/* Footer actions section */}
                      <div className="flex items-center justify-start gap-3 pt-4 border-t border-gray-200 mt-auto">
                        <Button variant="outline" onClick={onBoostPostCancel} className="w-24">
                          Cancel
                        </Button>
                        <Button className="w-24" type="submit">
                          Boost
                        </Button>
                      </div>
                    </form>
                  </Form>
                </TabsContent>
              </Tabs>
            </SheetColumn>
          </SheetGrid>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ContentPreviewSheetBoost;
